import { useState, useCallback, useRef, type ReactNode } from 'react'
import { Puck, createUsePuck, legacySideBarPlugin, type Data } from '@puckeditor/core'
import '@puckeditor/core/dist/index.css'
import { Template } from '../../../types'
import './PopupEditor.css'
import { migrateRichText, puckConfig } from './puck/config'
import { buildPopupHtml } from './puck/buildPopupHtml'
import Alert from '../../../ui/Alert'
import { useTranslation } from 'react-i18next'

interface PopupEditorProps {
    template: Template
    setTemplate: (template: Template | undefined) => void
}

const usePuck = createUsePuck()

const plugins = [legacySideBarPlugin()]

const dnd = { behavior: 'fluid', disableOutlineDrag: true } as const

const emptyData: Data = {
    content: [],
    root: {},
}

const positionStyles: Record<string, React.CSSProperties> = {
    center: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    bottom_right: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        padding: 12,
    },
    bottom_left: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-start',
        padding: 12,
    },
    top_bar: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'stretch',
    },
    full_screen: {
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
    },
}

function OutlineWithPage({ children }: { children: ReactNode }) {
    const dispatch = usePuck(s => s.dispatch)
    const isSelected = usePuck(s => s.appState.ui.itemSelector === null)
    return (
        <>
            <div className={`popup-outline-page${isSelected ? ' active' : ''}`}>
                <button
                    type="button"
                    onClick={() => dispatch({ type: 'setUi', ui: { itemSelector: null } })}
                    className="popup-outline-page-btn"
                >
                    <div className="popup-outline-page-title">
                        <div className="popup-outline-page-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            </svg>
                        </div>
                        <div className="popup-outline-page-name">Page</div>
                    </div>
                </button>
            </div>
            <div style={{ marginInlineStart: 5 }}>
                {children}
            </div>
        </>
    )
}

function PhonePreview({ children }: { children: ReactNode }) {
    const rootProps = usePuck(s => (s.appState.data.root as any).props) ?? {}
    const position: string = rootProps.position ?? 'center'
    const overlay: boolean = rootProps.overlay ?? true
    const maxWidth: number = rootProps.maxWidth ?? 480
    const borderRadius: number = rootProps.borderRadius ?? 12
    const isFullScreen = position === 'full_screen'
    const isTopBar = position === 'top_bar'

    return (
        <div className="popup-preview-wrapper">
            <div className="popup-phone-frame">
                <div className="popup-phone-screen" style={{ position: 'relative' }}>
                    <div className="popup-phone-app-bg" />
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            ...positionStyles[position],
                            background: overlay ? 'rgba(0, 0, 0, 0.4)' : 'transparent',
                        }}
                    >
                        <div style={{
                            width: '100%',
                            maxWidth: isFullScreen ? '100%' : maxWidth,
                            height: isFullScreen ? '100%' : undefined,
                            borderRadius: isFullScreen || isTopBar ? 0 : borderRadius,
                            overflow: 'hidden',
                            boxShadow: isFullScreen ? 'none' : '0 8px 32px rgba(0,0,0,0.2)',
                            ...(isFullScreen ? { display: 'flex', flexDirection: 'column' as const } : {}),
                        }}>
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

const overrides = {
    preview: PhonePreview,
    outline: OutlineWithPage,
}

export default function PopupEditor({ template, setTemplate }: PopupEditorProps) {
    const { t } = useTranslation()
    const [renderFailed, setRenderFailed] = useState(false)
    const [initialData] = useState<Data>(() => {
        const saved = template.data.puck_data
        if (saved?.content) {
            return migrateRichText(saved as Data)
        }
        return emptyData
    })

    const templateRef = useRef(template)
    templateRef.current = template

    const handleChange = useCallback((data: Data) => {
        const puckData = JSON.parse(JSON.stringify(data))
        const currentTemplate = templateRef.current
        let html = currentTemplate.data.html
        try {
            html = buildPopupHtml(data)
            setRenderFailed(false)
        } catch {
            setRenderFailed(true)
        }

        const updatedTemplate = {
            ...currentTemplate,
            data: {
                ...currentTemplate.data,
                puck_data: puckData,
                html,
            },
        }
        templateRef.current = updatedTemplate
        setTemplate(updatedTemplate)
    }, [setTemplate])

    return (
        <div className="popup-editor">
            {renderFailed && <div role="alert">
                <Alert
                    variant="error"
                    title={t('popup_render_error_title')}
                    body={t('popup_render_error_body')}
                />
            </div>}
            <Puck
                config={puckConfig}
                data={initialData}
                iframe={{ enabled: false }}
                plugins={plugins}
                dnd={dnd}
                onChange={handleChange}
                overrides={overrides}
            />
        </div>
    )
}
